import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n/i18n';

interface LanguageContextProps {
  currentLanguage: string;
  changeLanguage: (lng: string) => void;
  languages: { code: string; name: string }[];
}

const languages = [
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'en', name: 'English' },
];

const LanguageContext = createContext<LanguageContextProps | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    // Ensure only supported languages are used
    if (savedLang && ['vi', 'en'].includes(savedLang)) {
      return savedLang;
    }
    return 'vi'; // Default to Vietnamese
  });

  const changeLanguage = (lng: string) => {
    // Validate language code
    if (!['vi', 'en'].includes(lng)) {
      console.warn(`Unsupported language: ${lng}, falling back to 'vi'`);
      lng = 'vi';
    }
    
    i18n.changeLanguage(lng);
    setCurrentLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
    // Update HTML lang attribute for accessibility
    document.documentElement.setAttribute('lang', lng);
  };

  useEffect(() => {
    // Ensure i18n is initialized with correct language
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang && ['vi', 'en'].includes(savedLang)) {
      i18n.changeLanguage(savedLang);
      setCurrentLanguage(savedLang);
    } else {
      // Fallback to Vietnamese
      i18n.changeLanguage('vi');
      setCurrentLanguage('vi');
      localStorage.setItem('i18nextLng', 'vi');
    }
    
    // Set initial HTML lang attribute
    document.documentElement.setAttribute('lang', currentLanguage);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        languages,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextProps => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
